/* eslint-disable no-console */
import { datadogLogs } from '@datadog/browser-logs';
import { isClient } from '~/utils/misc';
import { desensitizeObjectValues } from './logger-utils';
import type { LogAttributes, Logger } from './logger.interface';
import { LogLevel } from './logger.interface';

/**
 * Create singleton client loggers.
 */
const loggers: Record<string, Logger> = {};

/**
 * Client-only: builds a DataDog logger using a singleton pattern to ensure
 * loggers are initialized only once.
 *
 * - User id, and host are associated with the logger. This is configured in
 *   the datadog client init.
 * - Logger can only be used in the client. It will be undefined during loaders, actions or SSR.
 */
export const createClientLogger = (name: string): Logger => {
  if (!loggers[name]) {
    loggers[name] = new ClientLogger(name);
  }

  return loggers[name];
};

class ClientLogger implements Logger {
  constructor(private name: string) {}

  fatal(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.FATAL, message, attributes);
  }

  critical(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.CRITICAL, message, attributes);
  }

  debug(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.DEBUG, message, attributes);
  }

  error(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.ERROR, message, attributes);
  }

  info(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.INFO, message, attributes);
  }

  trace(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.TRACE, message, attributes);
  }

  warn(message: string, attributes?: LogAttributes): void {
    this.log(LogLevel.WARN, message, attributes);
  }

  private log(level: LogLevel, message: string, attributes?: LogAttributes): void {
    if (!isClient()) {
      console.error('❌ Client-logger was called on the server. Try using the server-logger instead.', {
        name: this.name,
      });

      return;
    }

    /**
     * We can't log errors until DataDog client has been initialized in
     * `datadogLogs.init()`
     */
    const ddLogger = datadogLogs.logger;
    if (!ddLogger) {
      return;
    }

    /**
     * Redact sensitive information from attributes. These are then sent as a
     * separate object, which allows them to be queried in DataDog.
     */
    const desensitizedAttributes = attributes ? desensitizeObjectValues(attributes) : undefined;

    // The initial log message that appears in the "content" column in DataDog.
    const logArgs: string[] = [`[${this.name}]`];
    logArgs.push(message);
    if (attributes) {
      logArgs.push(JSON.stringify(desensitizedAttributes));
    }
    const logMessage = logArgs.join(' ');

    // Log to the browser for visibility during development
    if (process.env.NODE_ENV === 'development') {
      if (attributes) {
        console.log(logMessage, attributes);
      } else {
        console.log(logMessage);
      }
    }

    const messageContext = { attributes: desensitizedAttributes };

    switch (level) {
      case LogLevel.FATAL:
      case LogLevel.CRITICAL:
      case LogLevel.ERROR:
        ddLogger.error(logMessage, messageContext);
        break;
      case LogLevel.WARN:
        ddLogger.warn(logMessage, messageContext);
        break;
      case LogLevel.DEBUG:
      case LogLevel.TRACE: // DataDog doesn't have a trace method, so we log these as debug
        ddLogger.debug(logMessage, messageContext);
        break;
      case LogLevel.INFO:
      default:
        ddLogger.info(logMessage, messageContext);
        break;
    }
  }
}
